/**
 * The Evolution Tree — single source of truth for every life form (stage),
 * how they connect, and how the player leans toward each branch.
 *
 * Owned by the orchestrator. Stage agents may tweak copy for their own node
 * (name/tagline/hints/ending text) but must not change ids or edges without
 * coordinating.
 */

export type StageId =
  | 'cell'
  | 'algae'
  | 'multicell'
  | 'jellyfish'
  | 'fish'
  | 'shark'
  | 'amphibian'
  | 'reptile'
  | 'dinosaur'
  | 'bird'
  | 'mammal'
  | 'whale'
  | 'wolf'
  | 'primate'
  | 'chimp'
  | 'human';

/** Gameplay mode families. Stages in the same family feel related but each has its own twist. */
export type StageMode = 'microbe' | 'bloom' | 'drift' | 'swim' | 'land' | 'flight' | 'arboreal' | 'tribe';

export type NodeKind = 'stage' | 'terminal' | 'final';

export interface EvoPath {
  /** Destination stage. */
  to: StageId;
  /** Affinity key the stage adds points to when the player behaves this way. */
  key: string;
  /** Short name of the adaptation shown in the HUD mutation meter. */
  label: string;
  /** Hex color used for this branch in HUD/codex. */
  color: string;
  /** One-line hint telling the player how to lean this way. */
  hint: string;
}

export interface EvoEnding {
  title: string;
  text: string;
}

export interface EvoNode {
  id: StageId;
  name: string;
  tagline: string;
  era: string;
  kind: NodeKind;
  mode: StageMode;
  view: '2.5d' | '3d';
  /** Target play time in minutes for a competent player. */
  targetMinutes: number;
  /** Theme color (hex). */
  color: string;
  /** Branches out of this node. Empty for terminals/final. First path is the default. */
  paths: EvoPath[];
  /** Present for terminal/final nodes. */
  ending?: EvoEnding;
  /** Actions this stage uses, with context labels (drives key hints + touch buttons). */
  actions: Partial<Record<ActionLabelKey, string>>;
}

export type ActionLabelKey = 'attack' | 'dash' | 'jump' | 'ability' | 'interact';

export const ROOT_STAGE: StageId = 'cell';

export const TREE: Record<StageId, EvoNode> = {
  cell: {
    id: 'cell',
    name: 'Primordial Cell',
    tagline: 'A speck of life in a hostile soup.',
    era: '3.8 billion years ago',
    kind: 'stage',
    mode: 'microbe',
    view: '2.5d',
    targetMinutes: 2.5,
    color: '#7CFF6B',
    actions: { attack: 'Bash', dash: 'Dash', interact: 'Evolve' },
    paths: [
      {
        to: 'multicell',
        key: 'predator',
        label: 'Predation',
        color: '#FF5A7A',
        hint: 'Hunt and absorb other cells.',
      },
      {
        to: 'algae',
        key: 'photosynth',
        label: 'Photosynthesis',
        color: '#6BFFB8',
        hint: 'Bask in sunbeams and soak up green motes.',
      },
    ],
  },
  algae: {
    id: 'algae',
    name: 'Algal Bloom',
    tagline: 'Why chase food when the sun delivers?',
    era: '3.4 billion years ago',
    kind: 'terminal',
    mode: 'bloom',
    view: '2.5d',
    targetMinutes: 2.5,
    color: '#3DDC97',
    actions: { attack: 'Toxin', dash: 'Spread', ability: 'Bloom Burst', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Pond Scum Supreme',
      text: 'You blanketed the world ocean in green and pumped the sky full of oxygen. Every breath anything ever takes is your fault.',
    },
  },
  multicell: {
    id: 'multicell',
    name: 'Multicellular Colony',
    tagline: 'Strength in numbers. Literally.',
    era: '600 million years ago',
    kind: 'stage',
    mode: 'microbe',
    view: '2.5d',
    targetMinutes: 3,
    color: '#FF8A5B',
    actions: { attack: 'Bite', dash: 'Dash', ability: 'Sting', interact: 'Evolve' },
    paths: [
      {
        to: 'fish',
        key: 'vertebrate',
        label: 'Backbone',
        color: '#5BC8FF',
        hint: 'Chase prey with speed and jaws.',
      },
      {
        to: 'jellyfish',
        key: 'drifter',
        label: 'Stinging Drift',
        color: '#D78CFF',
        hint: 'Sting things and drift with the currents.',
      },
    ],
  },
  jellyfish: {
    id: 'jellyfish',
    name: 'Jellyfish',
    tagline: 'No brain. No problem.',
    era: '550 million years ago',
    kind: 'terminal',
    mode: 'drift',
    view: '3d',
    targetMinutes: 2.5,
    color: '#D78CFF',
    actions: { attack: 'Sting', dash: 'Pulse', ability: 'Glow', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Immortal Goo',
      text: 'You figured out how to age backwards and never bothered evolving again. Honestly? Smartest one here.',
    },
  },
  fish: {
    id: 'fish',
    name: 'Jawed Fish',
    tagline: 'Congratulations, you have a spine.',
    era: '420 million years ago',
    kind: 'stage',
    mode: 'swim',
    view: '3d',
    targetMinutes: 3,
    color: '#5BC8FF',
    actions: { attack: 'Bite', dash: 'Burst', jump: 'Rise', ability: 'Dive', interact: 'Evolve' },
    paths: [
      {
        to: 'amphibian',
        key: 'shore',
        label: 'Lungs & Limbs',
        color: '#9BE564',
        hint: 'Explore the shallows and gulp air at the surface.',
      },
      {
        to: 'shark',
        key: 'apex',
        label: 'Apex Predator',
        color: '#6C8CFF',
        hint: 'Hunt big prey in the deep.',
      },
    ],
  },
  shark: {
    id: 'shark',
    name: 'Shark',
    tagline: 'Perfected 400 million years ago. Never updated.',
    era: '380 million years ago',
    kind: 'terminal',
    mode: 'swim',
    view: '3d',
    targetMinutes: 2.5,
    color: '#6C8CFF',
    actions: { attack: 'Bite', dash: 'Charge', jump: 'Rise', ability: 'Sense Blood', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Apex of the Deep',
      text: 'You became so good at eating that evolution just stopped trying. Zero notes. Nothing to add.',
    },
  },
  amphibian: {
    id: 'amphibian',
    name: 'Amphibian',
    tagline: 'Half fish, half legs, fully confused.',
    era: '365 million years ago',
    kind: 'stage',
    mode: 'land',
    view: '3d',
    targetMinutes: 3,
    color: '#9BE564',
    actions: { attack: 'Tongue', dash: 'Leap', jump: 'Hop', interact: 'Evolve' },
    paths: [
      {
        to: 'reptile',
        key: 'dryland',
        label: 'Scaly Skin',
        color: '#E5B45B',
        hint: 'Push inland, away from the water.',
      },
    ],
  },
  reptile: {
    id: 'reptile',
    name: 'Reptile',
    tagline: 'Cold blood. Colder attitude.',
    era: '310 million years ago',
    kind: 'stage',
    mode: 'land',
    view: '3d',
    targetMinutes: 3,
    color: '#E5B45B',
    actions: { attack: 'Bite', dash: 'Scurry', jump: 'Jump', ability: 'Tail Whip', interact: 'Evolve' },
    paths: [
      {
        to: 'mammal',
        key: 'warm',
        label: 'Warm Blood',
        color: '#FFB36B',
        hint: 'Stay hidden, forage at night, keep warm in burrows.',
      },
      {
        to: 'dinosaur',
        key: 'giant',
        label: 'Gigantism',
        color: '#FF6B4A',
        hint: 'Eat everything. Get huge. Fight big.',
      },
      {
        to: 'bird',
        key: 'feather',
        label: 'Feathers',
        color: '#6BE0FF',
        hint: 'Climb high and glide between perches.',
      },
    ],
  },
  dinosaur: {
    id: 'dinosaur',
    name: 'Tyrannosaur',
    tagline: 'Big. Loud. Tiny arms. Absolutely no regrets.',
    era: '68 million years ago',
    kind: 'terminal',
    mode: 'land',
    view: '3d',
    targetMinutes: 2.5,
    color: '#FF6B4A',
    actions: { attack: 'Chomp', dash: 'Charge', ability: 'Roar', jump: 'Stomp', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Terrible Lizard',
      text: 'You ruled the Earth for 150 million years. Then a rock the size of a city ended the argument.',
    },
  },
  bird: {
    id: 'bird',
    name: 'Bird',
    tagline: 'Technically still a dinosaur. Emotionally free.',
    era: '150 million years ago',
    kind: 'terminal',
    mode: 'flight',
    view: '3d',
    targetMinutes: 3,
    color: '#6BE0FF',
    actions: { attack: 'Dive', dash: 'Boost', jump: 'Flap', ability: 'Screech', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Lord of the Skies',
      text: 'You took the dinosaur plan and added flight. The only dinosaurs that survived the rock. Smug about it too.',
    },
  },
  mammal: {
    id: 'mammal',
    name: 'Early Mammal',
    tagline: 'Small, furry, and extremely anxious.',
    era: '66 million years ago',
    kind: 'stage',
    mode: 'land',
    view: '3d',
    targetMinutes: 3,
    color: '#FFB36B',
    actions: { attack: 'Bite', dash: 'Scamper', jump: 'Jump', ability: 'Burrow', interact: 'Evolve' },
    paths: [
      {
        to: 'primate',
        key: 'arboreal',
        label: 'Grasping Hands',
        color: '#C98B5A',
        hint: 'Climb trees and forage for fruit.',
      },
      {
        to: 'wolf',
        key: 'pack',
        label: 'Pack Hunter',
        color: '#A0A8B8',
        hint: 'Hunt together and take down bigger prey.',
      },
      {
        to: 'whale',
        key: 'marine',
        label: 'Back to the Sea',
        color: '#4A7CFF',
        hint: 'Spend your time swimming and fishing in the water.',
      },
    ],
  },
  whale: {
    id: 'whale',
    name: 'Whale',
    tagline: 'Walked out of the ocean. Walked right back in.',
    era: '40 million years ago',
    kind: 'terminal',
    mode: 'swim',
    view: '3d',
    targetMinutes: 2.5,
    color: '#4A7CFF',
    actions: { attack: 'Gulp', dash: 'Dive', ability: 'Song', jump: 'Breach', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Leviathan',
      text: 'Millions of years of legs and you gave them back. Now you are the largest animal that has ever lived. Worth it.',
    },
  },
  wolf: {
    id: 'wolf',
    name: 'Wolf',
    tagline: 'Friendship is just a hunting strategy.',
    era: '1 million years ago',
    kind: 'terminal',
    mode: 'land',
    view: '3d',
    targetMinutes: 2.5,
    color: '#A0A8B8',
    actions: { attack: 'Bite', dash: 'Sprint', jump: 'Pounce', ability: 'Howl', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'Leader of the Pack',
      text: 'You mastered teamwork. In a few thousand years your descendants will fetch sticks for the apes. Enjoy it while it lasts.',
    },
  },
  primate: {
    id: 'primate',
    name: 'Primate',
    tagline: 'Opposable thumbs. Endless possibilities.',
    era: '6 million years ago',
    kind: 'stage',
    mode: 'arboreal',
    view: '3d',
    targetMinutes: 3,
    color: '#C98B5A',
    actions: { attack: 'Swipe', dash: 'Swing', jump: 'Leap', ability: 'Throw', interact: 'Evolve' },
    paths: [
      {
        to: 'human',
        key: 'tool',
        label: 'Tool Use',
        color: '#FFD86B',
        hint: 'Walk upright, pick up stones and sticks, use tools.',
      },
      {
        to: 'chimp',
        key: 'canopy',
        label: 'Canopy King',
        color: '#8BC34A',
        hint: 'Stay in the trees and dominate the troop.',
      },
    ],
  },
  chimp: {
    id: 'chimp',
    name: 'Chimpanzee',
    tagline: 'Ninety-eight point eight percent of the way there.',
    era: '4 million years ago',
    kind: 'terminal',
    mode: 'arboreal',
    view: '3d',
    targetMinutes: 2.5,
    color: '#8BC34A',
    actions: { attack: 'Smash', dash: 'Swing', jump: 'Leap', ability: 'Hoot', interact: 'Evolve' },
    paths: [],
    ending: {
      title: 'King of the Canopy',
      text: 'You looked at the ground, looked at the tools, and said: nah. No taxes, no emails. Who is the smart one now?',
    },
  },
  human: {
    id: 'human',
    name: 'Human',
    tagline: 'Fire. Tools. Immediately regrettable decisions.',
    era: '300,000 years ago',
    kind: 'final',
    mode: 'tribe',
    view: '3d',
    targetMinutes: 3.5,
    color: '#FFD86B',
    actions: { attack: 'Strike', dash: 'Sprint', jump: 'Jump', ability: 'Throw', interact: 'Use' },
    paths: [],
    ending: {
      title: 'The Apex Ape',
      text: 'Fire, tools, language, and eventually... a ray gun that turns people back into single cells. See you at the bottom.',
    },
  },
};

export const ALL_STAGES = Object.keys(TREE) as StageId[];

export const ENDING_STAGES = ALL_STAGES.filter((id) => TREE[id].kind !== 'stage');

export function isStageId(v: unknown): v is StageId {
  return typeof v === 'string' && v in TREE;
}

/** Parent of a node (undefined for the root). */
export function parentOf(id: StageId): StageId | undefined {
  for (const node of Object.values(TREE)) {
    if (node.paths.some((p) => p.to === id)) return node.id;
  }
  return undefined;
}

/** Path from root to the given node, inclusive. */
export function lineageOf(id: StageId): StageId[] {
  const out: StageId[] = [id];
  let cur = parentOf(id);
  while (cur) {
    out.unshift(cur);
    cur = parentOf(cur);
  }
  return out;
}

/** Sum of target minutes along the lineage to a node (inclusive). */
export function targetMinutesTo(id: StageId): number {
  return lineageOf(id).reduce((sum, s) => sum + TREE[s].targetMinutes, 0);
}

/**
 * Pick the branch the player leans toward. Ties and all-zero resolve to the
 * first path (the "default" branch). Returns undefined for terminals.
 */
export function resolveBranch(id: StageId, affinity: Record<string, number>): EvoPath | undefined {
  const node = TREE[id];
  if (node.paths.length === 0) return undefined;
  let best = node.paths[0];
  let bestScore = affinity[best.key] ?? 0;
  for (const p of node.paths.slice(1)) {
    const s = affinity[p.key] ?? 0;
    if (s > bestScore) {
      best = p;
      bestScore = s;
    }
  }
  return best;
}

/** Normalized affinity shares (0..1, summing to 1) for a node's paths. */
export function affinityShares(id: StageId, affinity: Record<string, number>): Record<string, number> {
  const node = TREE[id];
  const out: Record<string, number> = {};
  const total = node.paths.reduce((s, p) => s + Math.max(0, affinity[p.key] ?? 0), 0);
  for (const p of node.paths) {
    out[p.key] = total > 0 ? Math.max(0, affinity[p.key] ?? 0) / total : 1 / node.paths.length;
  }
  return out;
}
